import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppSettings } from '../app-settings';
import {
  SeasonPassSignupContext,
  SeasonPassSignupError,
  SeasonPassSignupPayload,
  SeasonPassSignupResponse,
} from '../interfaces/abonado';

/**
 * Alta de abonado en autoservicio desde la web pública. El hermano de
 * MembershipCardService (que renueva a los socios migrados a partir de la foto
 * de su carné) para quien NO tiene abono previo: aquí no hay ficha de la que
 * partir, así que el socio elige categoría y butaca.
 *
 * NINGUNA llamada de aquí lleva `id_season` fijo: la temporada la resuelve el
 * servidor (`OpenSeasonResolver`) y la devuelve en `getContext`, y de ahí sale
 * el id que se pasa a las siguientes. Antes iba hardcodeado en este fichero, lo
 * que obligaba a tocar el código y reconstruir la imagen en cada entorno y en
 * cada cambio de temporada.
 */
@Injectable({
  providedIn: 'root'
})
export class SeasonPassSignupService {

  constructor(private http: HttpClient) { }

  /**
   * Temporada, recinto y categorías elegibles. Se pide al abrir el formulario:
   * si falla, no hay alta posible (no se puede elegir categoría a ciegas).
   *
   * Sin `idSeason` el servidor resuelve la temporada abierta y la devuelve en
   * `season`, que es de donde el componente saca el id para las demás llamadas.
   * Se puede forzar una concreta (enlace a un club cuando haya más de una
   * temporada abierta, o el servidor responderá `SEASON_AMBIGUOUS`).
   */
  getContext(idSeason?: number): Observable<SeasonPassSignupContext> {
    const query = idSeason ? `?id_season=${idSeason}` : '';
    const url = `${AppSettings.API_URI_V2}/season-pass-signups/context${query}`;
    return this.http.get<any>(url).pipe(
      map((res) => res.data as SeasonPassSignupContext),
      catchError((err: HttpErrorResponse) => throwError(() => this.toSignupError(err))),
    );
  }

  /**
   * Mapa del recinto con las butacas ocupadas (abonos + socios legacy sin
   * renovar) y las bloqueadas del recinto, para que el socio elija la suya. No
   * trae `own_seat_id`: en un alta no hay butaca previa.
   */
  getSeats(idSeason?: number): Observable<any> {
    const query = idSeason ? `?id_season=${idSeason}` : '';
    const url = `${AppSettings.API_URI_V2}/season-pass-signups/seats${query}`;
    return this.http.get<any>(url).pipe(
      map((res) => res.data),
      catchError((err: HttpErrorResponse) => throwError(() => this.toSignupError(err))),
    );
  }

  /**
   * Crea el abono (pendiente de pago) y devuelve un JWT: el endpoint resuelve la
   * cuenta él mismo —la del token si hay sesión, y si no la del email, validando
   * su contraseña o creándola ya activa—, así que no hay que registrar ni
   * loguear aparte antes de mandar al socio al checkout.
   */
  signUp(payload: SeasonPassSignupPayload): Observable<SeasonPassSignupResponse> {
    const url = `${AppSettings.API_URI_V2}/season-pass-signups`;
    return this.http.post<any>(url, payload).pipe(
      map((res) => ({ seasonPass: res.data, auth: res.auth } as SeasonPassSignupResponse)),
      catchError((err: HttpErrorResponse) => throwError(() => this.toSignupError(err))),
    );
  }

  /**
   * Traduce la respuesta de error a un código que el wizard pueda tratar: cada
   * uno señala un campo distinto del formulario (contraseña, categoría, butaca),
   * así que un genérico no serviría.
   */
  private toSignupError(err: HttpErrorResponse): SeasonPassSignupError {
    const body = err?.error;
    const knownCodes = [
      'INVALID_CREDENTIALS',
      'SESSION_EXPIRED',
      'EMAIL_ALREADY_IN_USE',
      'USER_ALREADY_HAS_PASS',
      'SEAT_NOT_AVAILABLE',
      'SEAT_NOT_IN_SPACE',
      // Butaca libre de abonados pero ya vendida en un evento del club.
      'SEAT_SOLD_IN_EVENT',
      'CATEGORY_NOT_AVAILABLE',
      'SEASON_NOT_AVAILABLE',
      // Varias temporadas abiertas y ninguna pedida: el servidor no elige por
      // nosotros. Hoy no puede pasar (un solo club), pero si pasa el mensaje del
      // backend es lo único que explica qué falta.
      'SEASON_AMBIGUOUS',
      'SPACE_NOT_FOUND',
    ];

    if (knownCodes.includes(body?.error)) {
      return {
        code: body.error,
        message: body.message,
        seasonPass: body.data,
        auth: body.auth,
      };
    }

    if (err?.status === 422 && body?.errors) {
      const first: any = Object.values(body.errors)[0];
      return { code: 'VALIDATION', message: Array.isArray(first) ? first[0] : body.message };
    }

    return { code: 'GENERIC', message: body?.message || 'Ha ocurrido un error, vuelve a intentarlo.' };
  }
}
